import { useState } from 'react';
import type {
  Template,
  Audience,
  Tone,
  Channel,
  RewriteRequest,
  RewriteResponse,
} from '../types';
import { rewrite } from '../services/api';

type EditorState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; response: RewriteResponse }
  | { status: 'error'; message: string }
  | { status: 'limit_reached'; upgradeUrl: string }
  | { status: 'input_error'; message: string };

/**
 * Editor state for the Executive Polish tool.
 *
 * Usage is owned by useUserState — the single source of truth across tools.
 * useEditor never holds a usage count; it reports the new value through
 * onUsageChange after each rewrite so useUserState can update locally.
 */
export function useEditor(onUsageChange: (value: number | null) => void) {
  const [rawInput, setRawInput] = useState('');
  const [template, setTemplate] = useState<Template>('status_update');
  const [audience, setAudience] = useState<Audience>('executive');
  const [tone, setTone] = useState<Tone>('formal');
  const [channel, setChannel] = useState<Channel>('email');
  const [editorState, setEditorState] = useState<EditorState>({ status: 'idle' });

  const MAX_CHARS = 8000;

  async function submit() {
    if (!rawInput.trim()) {
      setEditorState({
        status: 'input_error',
        message: 'Please enter your draft communication',
      });
      return;
    }
    if (rawInput.length > MAX_CHARS) {
      setEditorState({
        status: 'input_error',
        message: `Input too long — maximum ${MAX_CHARS} characters`,
      });
      return;
    }

    setEditorState({ status: 'loading' });

    try {
      const request: RewriteRequest = {
        raw_input: rawInput,
        template,
        audience,
        tone,
        channel,
      };
      const response = await rewrite(request);
      onUsageChange(response.rewrites_remaining ?? null);
      setEditorState({ status: 'success', response });
    } catch (err: unknown) {
      const error = err as { code?: string; upgradeUrl?: string };
      if (error.code === 'LIMIT_REACHED') {
        onUsageChange(0);
        setEditorState({
          status: 'limit_reached',
          upgradeUrl: error.upgradeUrl ?? '/upgrade',
        });
      } else {
        setEditorState({
          status: 'error',
          message: 'Something went wrong. Your input is preserved — please try again.',
        });
      }
    }
  }

  function reset() {
    setEditorState({ status: 'idle' });
  }

  return {
    rawInput,
    setRawInput,
    template,
    setTemplate,
    audience,
    setAudience,
    tone,
    setTone,
    channel,
    setChannel,
    editorState,
    submit,
    reset,
    MAX_CHARS,
  };
}
